import { useState, useEffect, useCallback, createContext, useContext, useRef, type ReactNode } from 'react';
import type { User, Expense, Settlement, BankAccount, ViewType, GroupMember } from '../types';
import { api, type AuthSessionResponse, resetUnauthorizedFlag } from '../services/api';

interface LedgerState {
  view: ViewType;
  setView: (view: ViewType) => void;
  currentUser: User | null;
  expenses: Expense[];
  settlements: Settlement[];
  accounts: BankAccount[];
  users: User[];
  notifications: unknown[];
  unreadNotificationCount: number;
  editingExpense: Expense | null;
  setEditingExpense: (exp: Expense | null) => void;
  isLoading: boolean;
  isHydrated: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  register: (email: string, nickname: string) => Promise<{ success: boolean; fallbackLink?: string }>;
  applyAuthSession: (session: { token?: string; user: User }) => void;
  addExpense: (formData: FormData) => Promise<void>;
  updateExpense: (id: string, data: FormData) => Promise<void>;
  deleteExpense: (id: string) => Promise<void>;
  searchUser: (email: string) => Promise<User | null>;
  inviteMember: (userId: string) => Promise<void>;
  settle: (
    fromUserId: string | FormData,
    toUserId?: string,
    amount?: number,
    participantIds?: string[],
    receiptUrl?: string,
  ) => Promise<void>;
  sendReminder: (debtorId: string, amount: number, expenseTitle: string) => Promise<void>;
  refresh: () => Promise<void>;
  updateCurrentUser: (updatedUser: User) => void;
  currentGroupId: string | null;
  setCurrentGroupId: (id: string) => void;
  isSystemAdmin: boolean;
  groupMembers: GroupMember[];
  updateMemberRole: (groupId: string, userId: string, role: 'admin' | 'member') => Promise<void>;
  removeMember: (groupId: string, userId: string) => Promise<void>;
  refreshGroupMembers: (groupId: string) => Promise<void>;
}

const LedgerContext = createContext<LedgerState | undefined>(undefined);

/** SSR 安全的 localStorage 讀取（伺服器端回傳 null） */
const isBrowser = typeof window !== 'undefined';
const AUTH_FLAP_WINDOW_MS = 1000;
const AUTH_LOCK_MS = 30 * 1000;
const AUTH_LAST_EVENT_KEY = 'ledger_auth_last_event';
const AUTH_LOCK_UNTIL_KEY = 'ledger_auth_lock_until';

/** 將 ViewType 對應至 URL 路徑 */
const VIEW_URL_MAP: Record<ViewType, string> = {
  dashboard: '/',
  login: '/login',
  register: '/register',
  expenses: '/expenses',
  'add-expense': '/add-expense',
  'edit-expense': '/add-expense',
  settlements: '/settlements',
  accounts: '/accounts',
  members: '/members',
  search: '/search',
  history: '/history',
  'system-admin': '/system-admin',
};

/**
 * 功能：提供家庭帳本全域狀態（登入、支出、通知等），支援 SSR 初始視圖
 * @param {{ children: ReactNode; initialView?: ViewType }} props React 子節點與 SSR 初始視圖
 * @returns {JSX.Element} Context Provider
 */
export function LedgerProvider({ children, initialView = 'dashboard' }: { children: ReactNode; initialView?: ViewType }) {
  const [view, setViewState] = useState<ViewType>(initialView);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isHydrated, setIsHydrated] = useState(false);

  const setAuthLock = useCallback((until: number) => {
    if (!isBrowser) return;
    localStorage.setItem(AUTH_LOCK_UNTIL_KEY, String(until));
  }, []);

  const clearAuthLock = useCallback(() => {
    if (!isBrowser) return;
    localStorage.removeItem(AUTH_LOCK_UNTIL_KEY);
  }, []);

  const recordAuthEvent = useCallback((type: 'login' | 'logout'): boolean => {
    if (!isBrowser) return false;
    const now = Date.now();
    const raw = localStorage.getItem(AUTH_LAST_EVENT_KEY);
    if (raw) {
      try {
        const prev = JSON.parse(raw) as { type: 'login' | 'logout'; at: number };
        if (prev.type !== type && now - prev.at <= AUTH_FLAP_WINDOW_MS) {
          setAuthLock(now + AUTH_LOCK_MS);
          localStorage.removeItem('ledger_token');
          localStorage.removeItem('ledger_user');
          localStorage.removeItem('ledger_groupId');
          return true;
        }
      } catch {
        // ignore parse failures
      }
    }
    localStorage.setItem(AUTH_LAST_EVENT_KEY, JSON.stringify({ type, at: now }));
    return false;
  }, [setAuthLock]);

  // 還原 localStorage 狀態（僅在客戶端 mount 後執行，確保 SSR hydration 一致）
  useEffect(() => {
    const lockUntil = Number(localStorage.getItem(AUTH_LOCK_UNTIL_KEY) || '0');
    if (lockUntil > Date.now()) {
      localStorage.removeItem('ledger_token');
      localStorage.removeItem('ledger_user');
      localStorage.removeItem('ledger_groupId');
      setIsHydrated(true);
      return;
    }
    if (lockUntil > 0) clearAuthLock();
    const savedToken = localStorage.getItem('ledger_token');
    const savedUser = localStorage.getItem('ledger_user');
    const savedGroupId = localStorage.getItem('ledger_groupId');
    if (savedToken) setToken(savedToken);
    if (savedUser) {
      try { setCurrentUser(JSON.parse(savedUser)); } catch { /* ignore */ }
    }
    if (savedGroupId) setCurrentGroupIdState(savedGroupId);
    setIsHydrated(true);
  }, [clearAuthLock]);

  /** 切換視圖並同步更新瀏覽器 URL（pushState） */
  const setView = useCallback((newView: ViewType) => {
    setViewState(newView);
    if (isBrowser) {
      const url = VIEW_URL_MAP[newView] ?? '/';
      window.history.pushState(null, '', url);
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }, []);

  /** 監聽瀏覽器上一頁／下一頁，同步視圖狀態 */
  const setViewRef = useRef(setViewState);
  useEffect(() => {
    setViewRef.current = setViewState;
  });
  useEffect(() => {
    if (!isBrowser) return;
    const handlePopState = () => {
      const path = window.location.pathname;
      const urlViewMap: Record<string, ViewType> = {
        '/': 'dashboard',
        '/expenses': 'expenses',
        '/add-expense': 'add-expense',
        '/settlements': 'settlements',
        '/accounts': 'accounts',
        '/members': 'members',
        '/search': 'search',
        '/history': 'history',
      };
      const newView = urlViewMap[path] ??
        (path.startsWith('/expenses/') ? 'expenses' :
         path.startsWith('/settlements/') ? 'settlements' :
         path.startsWith('/history/') ? 'history' : 'dashboard');
      setViewRef.current(newView);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [settlements, setSettlements] = useState<Settlement[]>([]);
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [notifications, setNotifications] = useState<unknown[]>([]);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentGroupId, setCurrentGroupIdState] = useState<string | null>(null);
  const [groupMembers, setGroupMembers] = useState<GroupMember[]>([]);

  /** Persist group selection */
  const setCurrentGroupId = useCallback((id: string) => {
    setCurrentGroupIdState(id);
    if (isBrowser) localStorage.setItem('ledger_groupId', id);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('ledger_token');
    localStorage.removeItem('ledger_user');
    localStorage.removeItem('ledger_groupId');
    setCurrentUser(null);
    setToken(null);
    setView('dashboard');
    recordAuthEvent('logout');
  }, [recordAuthEvent, setView]);

  // Listen for server-initiated 401s (session expired) and clear auth state
  // without a full page reload, preventing the multiple-reload loop.
  useEffect(() => {
    if (!isBrowser) return;
    const handleUnauthorized = () => {
      setToken(null);
      setCurrentUser(null);
    };
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, [setToken, setCurrentUser]);

  const fetchData = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const [expList, settleHistory, accountList, notifList, groupsResp] = await Promise.all([
        api.expenses.list(currentGroupId ?? undefined),
        api.settle.history(currentGroupId ?? undefined),
        api.accounts.list(),
        api.notifications.list({ limit: 10, offset: 0 }),
        api.groups.list(),
      ]);
      setExpenses(expList as Expense[]);
      setSettlements(settleHistory as Settlement[]);
      setAccounts(accountList as BankAccount[]);
      const notifPage = notifList as { items?: unknown[]; unreadCount?: number } | null;
      setNotifications(notifPage?.items ?? []);
      setUnreadNotificationCount(notifPage?.unreadCount ?? 0);

      // 取最早加入的群組（後端已依 gm.joinedAt ASC 排序），但優先使用已儲存的 groupId
      const groups = (groupsResp as { groups: { id: string }[] } | null)?.groups ?? [];
      if (groups.length > 0) {
        const savedGid = isBrowser ? localStorage.getItem('ledger_groupId') : null;
        const validSaved = savedGid && groups.some((g) => g.id === savedGid);
        const gid = validSaved ? (savedGid as string) : groups[0].id;
        setCurrentGroupIdState(gid);
        if (!validSaved && isBrowser) localStorage.setItem('ledger_groupId', gid);
        try {
          const membersResp = await api.groups.listMembers(gid);
          setGroupMembers(membersResp?.members ?? []);
          setUsers((membersResp?.members ?? []).map((member) => ({
            id: member.userId,
            email: member.email,
            nickname: member.nickname,
            avatarUrl: member.avatarUrl,
          })) as User[]);
        } catch {
          // 群組成員載入失敗不阻斷整體流程
        }
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [token, currentGroupId]);

  const updateCurrentUser = (updatedUser: User) => {
    // Append cache-busting timestamp to force browser to reload the latest avatar image
    const userToSave = updatedUser.avatarUrl && !updatedUser.avatarUrl.startsWith('data:')
      ? { ...updatedUser, avatarUrl: updatedUser.avatarUrl.split('?')[0] + '?t=' + Date.now() }
      : updatedUser;
    setCurrentUser(userToSave);
    localStorage.setItem('ledger_user', JSON.stringify(userToSave));
  };

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  /**
   * 功能：以 email／密碼登入並寫入 localStorage
   * @param {string} email 帳號 email
   * @param {string} password 密碼
   * @returns {Promise<boolean>} 是否成功
   */
  const login = async (email: string, password: string) => {
    try {
      clearAuthLock();
      const resp = (await api.auth.login({ email, password })) as AuthSessionResponse;
      if (recordAuthEvent('login')) {
        setToken(null);
        setCurrentUser(null);
        return false;
      }
      resetUnauthorizedFlag();
      localStorage.setItem('ledger_token', resp.token);
      localStorage.setItem('ledger_user', JSON.stringify(resp.user));
      setToken(resp.token);
      setCurrentUser(resp.user);
      return true;
    } catch (error) {
      console.error('Login failed:', error);
      return false;
    }
  };

  const applyAuthSession = (session: { token?: string; user: User }) => {
    clearAuthLock();
    if (recordAuthEvent('login')) {
      setToken(null);
      setCurrentUser(null);
      return;
    }
    resetUnauthorizedFlag();
    // WebAuthn/magic-link endpoints are cookie-based and don't return a token.
    // Use the user-id as a sentinel so !!token stays truthy and fetchData runs.
    const tokenToStore = session.token ?? `cookie:${session.user.id}`;
    localStorage.setItem('ledger_token', tokenToStore);
    localStorage.setItem('ledger_user', JSON.stringify(session.user));
    setToken(tokenToStore);
    setCurrentUser(session.user);
  };

  const register = async (email: string, nickname: string) => {
    try {
      return (await api.auth.register({ email, nickname })) ?? { success: false };
    } catch (error) {
      console.error('Registration failed:', error);
      return { success: false };
    }
  };

  const addExpense = async (formData: FormData) => {
    try {
      const participantsStr = formData.get('participants') as string;
      const payerId = formData.get('payerId') as string;

      if (!participantsStr || !payerId) {
        throw new Error('Missing participants or payerId data');
      }

      const participants = JSON.parse(participantsStr) as { userId: string }[];

      if (!Array.isArray(participants)) {
        throw new Error('Participants data is not an array');
      }

      const enhancedParticipants = participants.map((p) => ({
        ...p,
        isPaid: p.userId === payerId,
      }));

      formData.set('participants', JSON.stringify(enhancedParticipants));
      if (currentGroupId) formData.set('groupId', currentGroupId);

      await api.expenses.create(formData);
      await fetchData();
    } catch (error) {
      console.error('Add expense failed:', error);
      throw error;
    }
  };

  const searchUser = async (email: string) => {
    try {
      return (await api.users.search(email)) as User;
    } catch (error) {
      console.error('Search user failed:', error);
      return null;
    }
  };

  const inviteMember = async (userId: string) => {
    try {
      await api.users.invite(userId);
      await fetchData();
    } catch (error) {
      console.error('Invite member failed:', error);
    }
  };

  const updateExpense = async (id: string, data: FormData) => {
    try {
      if (currentGroupId) data.set('groupId', currentGroupId);
      await api.expenses.update(id, data);
      await fetchData();
      setEditingExpense(null);
    } catch (error) {
      console.error('Update expense failed:', error);
      throw error;
    }
  };

  const deleteExpense = async (id: string) => {
    try {
      await api.expenses.delete(id, currentGroupId ?? undefined);
      await fetchData();
    } catch (error) {
      console.error('Delete expense failed:', error);
      throw error;
    }
  };

  const settleAction = async (
    fromUserId: string | FormData,
    toUserId?: string,
    amount?: number,
    participantIds?: string[],
    receiptUrl?: string,
  ) => {
    try {
      if (fromUserId instanceof FormData) {
        if (currentGroupId) fromUserId.set('groupId', currentGroupId);
        await api.settle.settle(fromUserId);
      } else {
        await api.settle.settle({
          fromUserId,
          toUserId,
          totalAmount: amount,
          participantIds,
          receiptUrl,
          groupId: currentGroupId,
        });
      }
      await fetchData();
    } catch (error) {
      console.error('Settlement failed:', error);
      throw error;
    }
  };

  const refreshGroupMembers = useCallback(async (groupId: string) => {
    try {
      const resp = await api.groups.listMembers(groupId);
      setGroupMembers(resp?.members ?? []);
    } catch (error) {
      console.error('Failed to refresh group members:', error);
    }
  }, []);

  const updateMemberRole = async (groupId: string, userId: string, role: 'admin' | 'member') => {
    await api.groups.updateMemberRole(groupId, userId, role);
    await refreshGroupMembers(groupId);
  };

  const removeMember = async (groupId: string, userId: string) => {
    await api.groups.removeMember(groupId, userId);
    await refreshGroupMembers(groupId);
  };

  const sendReminder = async (debtorId: string, amount: number, expenseTitle: string) => {
    try {
      await api.notifications.remind({ debtorId, amount, expenseTitle });
    } catch (error) {
      console.error('Send reminder failed:', error);
      throw error;
    }
  };

  const value: LedgerState = {
    view,
    setView,
    currentUser,
    expenses,
    settlements,
    accounts,
    users,
    notifications,
    unreadNotificationCount,
    editingExpense,
    setEditingExpense,
    isLoading,
    isHydrated,
    isAuthenticated: !!token,
    login,
    logout,
    register,
    applyAuthSession,
    addExpense,
    updateExpense,
    deleteExpense,
    searchUser,
    inviteMember,
    settle: settleAction,
    sendReminder,
    refresh: fetchData,
    updateCurrentUser,
    currentGroupId,
    setCurrentGroupId,
    isSystemAdmin: currentUser?.systemRole === 'system_admin',
    groupMembers,
    updateMemberRole,
    removeMember,
    refreshGroupMembers,
  };

  return <LedgerContext.Provider value={value}>{children}</LedgerContext.Provider>;
}

/**
 * 功能：讀取 LedgerProvider 提供的家庭帳本狀態
 * @returns {LedgerState} 狀態與方法集合
 */
export function useLedgerStore(): LedgerState {
  const context = useContext(LedgerContext);
  if (context === undefined) {
    throw new Error('useLedgerStore must be used within a LedgerProvider');
  }
  return context;
}
